<template>
    <common-modal :showCloseIcon="false" width="base">
        <slot>
            <common-content-spacer height="tiny" @click.stop>
                <p v-show="modalInfo" class="confirm-reservation__info">
                    {{ modalInfo }}
                </p>
                <img class="confirm-reservation__image" :src="calendarImage" :alt="$t('alt.reservation')" />
                <h4 class="confirm-reservation__heading">
                    <common-info-tip>
                        <p v-for="(item, index) in getCategoriesTitle" :key="index">{{ item }}</p>
                    </common-info-tip>
                    {{ getTranslation(offer?.titleTextContent) }}
                </h4>

                <h5 v-show="modalSubtitle" :class="subtitleClass">
                    {{ modalSubtitle }}
                </h5>
                <div v-show="!isConfirmed">
                    <common-content-spacer height="small" @click.stop>
                        <div>
                            <div class="confirm-reservation__parent">
                                <div class="confirm-reservation__parent--icon">
                                    <common-icon name="location-purple" size="super-large"></common-icon>
                                </div>
                                <div>
                                    <p class="confirm-reservation__parent--heading">
                                        {{ $t('modals.confirm_reservation_modal.location') }}
                                    </p>
                                    <p class="confirm-reservation__parent--text">
                                        {{ offer.location.address }}, {{ offer.location.city.name }}
                                    </p>
                                </div>
                            </div>
                            <div class="confirm-reservation__parent">
                                <div class="confirm-reservation__parent--icon">
                                    <common-icon name="calendar-purple" size="super-large"></common-icon>
                                </div>
                                <div>
                                    <p class="confirm-reservation__parent--heading">
                                        {{ $t('modals.confirm_reservation_modal.date_and_time') }}
                                    </p>
                                    <p class="confirm-reservation__parent--text">
                                        {{ dateFormatted }} | {{ timeFrom }} - {{ timeTo }}
                                    </p>
                                </div>
                            </div>
                            <div class="confirm-reservation__parent confirm-reservation__parent">
                                <div class="confirm-reservation__parent--icon">
                                    <common-icon name="price-purple" size="super-large"></common-icon>
                                </div>
                                <div>
                                    <p class="confirm-reservation__parent--heading">
                                        {{ $t('modals.confirm_reservation_modal.price') }}
                                    </p>
                                    <div :class="priceClass">
                                        <p
                                            v-if="priceHasDiscount"
                                            class="confirm-reservation__parent--text confirm-reservation__parent--text--original-price"
                                        >
                                            {{ offerPrice }}
                                        </p>
                                        <p class="confirm-reservation__parent--text">
                                            {{ offerDiscountPrice }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="showClientAc" class="confirm-reservation--clients">
                            <checkbox-input
                                class="confirm-reservation--clients--toggle"
                                :label="secondLabel"
                                :secondLabel="$t('pages.company.company_clients.add_new_client')"
                                type="switch"
                                labelPosition="left"
                                :value="isClientSearchPreview"
                                @change="isClientSearchPreview = !isClientSearchPreview"
                            ></checkbox-input>
                            <common-client-search-autocomplete
                                v-show="!isClientSearchPreview"
                                ref="clientAC"
                                :companyId="offer.location.companyServiceProvider.id"
                                @client-selected="onClientSelected"
                                @client-removed="onClientRemoved"
                                @toggle-client-preview="isClientSearchPreview = !isClientSearchPreview"
                            ></common-client-search-autocomplete>
                            <add-client-form
                                v-show="isClientSearchPreview"
                                ref="clientForm"
                                :companyId="offer.location.companyServiceProvider.id"
                                :showDescription="false"
                                :showSubmitButton="false"
                                @client-added="onClientAdded"
                                @client-already-exist="onClientAlreadyExist"
                            ></add-client-form>
                        </div>

                        <textarea-input
                            v-if="!blockPublicReservations"
                            :placeholder="$t('modals.confirm_reservation_modal.add_note')"
                            type="text"
                            :maxlength="5000"
                            :prefillVal="description"
                            @change="descriptionChange"
                        ></textarea-input>
                        <p v-if="blockPublicReservations" class="confirm-reservation--phone">
                            {{ $t('modals.confirm_reservation_modal.blocked_reservation') }}
                            <a :href="`tel:${offer?.location?.telephones[0]?.telephone}`">
                                {{ offer?.location?.telephones[0]?.telephone }}
                            </a>
                        </p>
                        <div>
                            <common-buttons>
                                <common-button
                                    type="secondary"
                                    :isProcessing="isLoading"
                                    :isSubmittable="false"
                                    :isFluid="false"
                                    :label="$t('common.microcopy.cancel')"
                                    @click="emitEvent({ name: 'close' })"
                                ></common-button>
                                <a
                                    v-if="blockPublicReservations"
                                    :href="`tel:${offer?.location?.telephones[0]?.telephone}`"
                                    class="confirm-reservation--phone--action"
                                >
                                    <common-button
                                        type="primary"
                                        :isSubmittable="false"
                                        :isFluid="false"
                                        :label="$t('common.microcopy.invite')"
                                    ></common-button>
                                </a>
                                <common-button
                                    v-else
                                    type="primary"
                                    :isProcessing="isLoading"
                                    :isSubmittable="false"
                                    :isFluid="false"
                                    :label="$t('common.microcopy.confirm')"
                                    @click="checkClient()"
                                ></common-button>
                            </common-buttons>
                        </div>
                    </common-content-spacer>
                </div>
                <div v-show="isConfirmed">
                    <common-button
                        type="primary"
                        size="large"
                        :isProcessing="isLoading"
                        :isSubmittable="false"
                        :isFluid="true"
                        :label="$t('common.microcopy.done')"
                        @click="finishReservation()"
                    ></common-button>
                </div>
            </common-content-spacer>
        </slot>
        <common-yes-no-modal
            v-if="isYesNoModalOpen"
            :showWarningIcon="true"
            height="tiny"
            :headingText="$t('pages.company.company_clients.client_not_selected')"
            :noButtonText="$t('common.microcopy.back')"
            :yesButtonText="$t('common.microcopy.confirm')"
            @close="closeYesNoModal"
            @continue="continueReservation"
        ></common-yes-no-modal>
    </common-modal>
</template>

<script>
    import CalendarEventAPI from 'api/CalendarEventAPI'
    import availabilityTransformer from 'transformers/availabilityTransformer'
    import RoutesEnum from 'enums/routes.enum'
    import dayjs from 'dayjs'
    import PriceMixin from 'mixins/PriceMixin'
    import CalendarEventEnums from 'enums/calendar_event.enum'
    import AvailabilityEnums from 'enums/availability.enum'
    import { isLocalTimeRight } from '~/helpers'
    import TranslateMixin from 'mixins/TranslateMixin'
    import TextareaInput from 'components/form-inputs/TextareaInput'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'
    import AddClientForm from 'components/forms/company/AddClientForm'
    import OfferReservationTypeEnum from 'enums/offer_reservation_type.enum'

    export default {
        mixins: [PriceMixin, TranslateMixin],

        components: { TextareaInput, CheckboxInput, AddClientForm },

        props: {
            offer: { type: Object, required: true },
            time: { type: Object, required: true },
            date: { type: String, required: true },
            availabilityOfferId: { type: Number, required: true },
            categories: { type: Array, required: true },
            calendarEventId: { type: [Number, String], default: undefined },
            freeSchedule: { type: Boolean, required: true },
            moderatorNote: { type: String, default: undefined },
        },

        data: () => ({
            description: null,
            isLoading: false,
            isConfirmed: false,
            reservationCalendarImage: require('../../../assets/images/calendar.png'),
            confirmedCalendarImage: require('../../../assets/images/calendar-approved.png'),
            newReservationId: null,
            isYesNoModalOpen: false,
            isClientSearchPreview: false,
        }),

        computed: {
            secondLabel() {
                return `${this.$t('pages.company.company_clients.search_clients')} ${this.$t(
                    'pages.company.company_clients.clients',
                )}`
            },

            timeFrom() {
                return availabilityTransformer.minutesDateToLocalTZTime(this.time.from, this.date, this.offer.location.timeZone)
            },
            timeTo() {
                return availabilityTransformer.minutesDateToLocalTZTime(this.time.to, this.date, this.offer.location.timeZone)
            },
            getCategoriesTitle() {
                return this.categories?.map((item) => {
                    if (this.getTranslation(item?.titleTextContent).length > 20) {
                        return `${this.getTranslation(item?.titleTextContent).substring(0, 20)}...`
                    } else {
                        return this.getTranslation(item?.titleTextContent)
                    }
                })
            },
            offerPrice() {
                if (this.offer.hidePrice) return this.offer.currency
                const price = `${this.setPrice(
                    this.categories.reduce((acc, curr) => acc + curr[`price${this.offer.currency}`], 0),
                )} ${this.offer.currency}`

                return price
            },
            offerDiscountPrice() {
                if (this.offer.hidePrice) return this.offer.currency

                const price = `${this.setPrice(
                    this.categories.reduce((acc, curr) => acc + curr[`discountedPrice${this.offer.currency}`], 0),
                )} ${this.offer.currency}`

                return price
            },

            priceClass() {
                return {
                    'confirm-reservation__parent--hidden': this.offer.hidePrice,
                }
            },

            blockPublicReservations() {
                return (
                    this.offer.type === OfferReservationTypeEnum.BLOCK_PUBLIC_RESERVATIONS &&
                    !this.offer?.availabilityOffers
                        .find((item) => item.id === this.availabilityOfferId)
                        .availability?.availabilityUsers?.some((item) => item?.user?.id === this.$auth.getUser().id)
                )
            },

            priceHasDiscount() {
                return (
                    this.categories.reduce((acc, curr) => acc + curr.priceEUR, 0) !==
                    this.categories.reduce((acc, curr) => acc + curr.discountedPriceEUR, 0)
                )
            },

            dateFormatted() {
                return dayjs(this.date).format('DD.MM.YYYY')
            },
            userFullname() {
                return `${this.$auth.getUser().firstName} ${this.$auth.getUser().lastName}`
            },
            modalHeading() {
                return this.isConfirmed
                    ? this.calendarEventId
                        ? this.$t('modals.confirm_reservation_modal.reschedule_succeeded')
                        : this.$t('modals.confirm_reservation_modal.reservation_confirmed_heading')
                    : this.offer.titleTextContent.originalText
            },
            modalSubtitle() {
                if (this.isConfirmed) {
                    return this.getConfirmedSubtitle()
                } else {
                    return this.userFullname
                }
            },
            subtitleClass() {
                return {
                    'confirm-reservation__subtitle': true,
                    'confirm-reservation__subtitle--color': !this.isConfirmed,
                }
            },
            modalInfo() {
                return this.isConfirmed
                    ? this.calendarEventId
                        ? null
                        : this.$t('modals.confirm_reservation_modal.reservation_confirmed_info')
                    : this.offer.titleTextContent.originalText
            },

            calendarImage() {
                return this.isConfirmed ? this.confirmedCalendarImage : this.reservationCalendarImage
            },

            showClientAc() {
                return (
                    this.offer?.location?.companyServiceProvider?.id &&
                    !this.calendarEventId &&
                    this.offer?.availabilityOffers
                        .find((item) => item.id === this.availabilityOfferId)
                        .availability?.availabilityUsers?.some((item) => item?.user?.id === this.$auth.getUser().id)
                )
            },
        },

        methods: {
            getConfirmedSubtitle() {
                if (this.calendarEventId) {
                    return null
                } else if (this.showClientAc) {
                    return this.$t('modals.confirm_reservation_modal.owner_reservation_confirmed_subtitle')
                } else {
                    return this.$t('modals.confirm_reservation_modal.reservation_confirmed_subtitle')
                }
            },
            checkClient() {
                if (this.showClientAc && !this.userId && this.$refs.clientAC?.searchTerm?.length && !this.isClientSearchPreview) {
                    this.isYesNoModalOpen = true
                    return
                }
                if (this.isClientSearchPreview) {
                    this.$refs.clientForm.submit()
                    return
                }
                this.submit()
            },

            onClientAdded(payload) {
                this.userId = payload
                this.submit()
            },

            submit() {
                this.isLoading = true
                if (this.calendarEventId) {
                    this.reschedule()
                } else {
                    this.createReservation()
                }
            },

            onClientSelected(payload) {
                this.userId = payload
            },

            onClientRemoved() {
                this.userId = undefined
            },

            onClientAlreadyExist(payload) {
                this.isClientSearchPreview = false
                this.$refs.clientAC.searchTerm = payload.email ? payload.email : payload.mobile
                this.$refs.clientAC.inputChange()
            },

            createReservation() {
                CalendarEventAPI.createReservation({
                    date: this.date,
                    availabilityOfferId: this.availabilityOfferId,
                    from: this.time.from,
                    description: this.description,
                    offerCategoryIds: this.categories.map((item) => item.id),
                    freeSchedule: this.freeSchedule,
                    userId: this.userId,
                })
                    .then((res) => {
                        this.newReservationId = res.id
                        this.isConfirmed = true
                        this.isLoading = false
                        this.emitEvent({ name: 'reservation-confirmed' })
                    })
                    .catch((error) => {
                        if (error.message === CalendarEventEnums.ERRORS.CALENDAR_EVENT_BLOCKED) {
                            this.$toast.error(this.$t('modals.confirm_reservation_modal.user_blocked'))
                            this.emitEvent({ name: 'reservation-failed' })
                            this.isLoading = false
                            return
                        }

                        let calendarEventError
                        if (error?.error?.invalidFields) {
                            calendarEventError = error?.error?.invalidFields[0]
                        }

                        if (
                            calendarEventError?.message === AvailabilityEnums.ERRORS.UNKOWN_AVAILABILITY_OFFER &&
                            calendarEventError?.field === 'availabilityOfferId'
                        ) {
                            this.$toast.error(this.$t('common.errors.common_error'))
                            this.$router.push({ name: RoutesEnum.INDEX.HOME })
                            return
                        }
                        if (calendarEventError?.field === 'from') {
                            if (calendarEventError?.message.includes(CalendarEventEnums.ERRORS.MINIMUM_DIFFERENCE_IS_)) {
                                const matchedString = calendarEventError?.message.match(/(\d+)min/)
                                const minutes = matchedString[1]

                                this.$toast.error(this.$t(`modals.confirm_reservation_modal.mins_ahead_${minutes}`))
                            } else if (calendarEventError?.message === CalendarEventEnums.ERRORS.UNAVAILABLE_SLOT) {
                                this.$toast.error(this.$t('pages.calendar.my_calendar.unavailable_slot'))
                            } else {
                                this.$toast.error(this.$t('common.errors.common_error'))
                            }
                        } else {
                            this.$toast.error(this.$t('modals.confirm_reservation_modal.slot_taken'))
                        }

                        this.emitEvent({ name: 'reservation-failed' })
                        this.isLoading = false
                    })
            },

            finishReservation() {
                this.calendarEventId
                    ? this.$router.push({ name: RoutesEnum.RESERVATIONS.SINGLE, params: { id: this.calendarEventId } })
                    : this.$router.push({ name: RoutesEnum.RESERVATIONS.SINGLE, params: { id: this.newReservationId } })
            },

            reschedule() {
                CalendarEventAPI.reschedule({
                    date: this.date,
                    calendarEventId: Number(this.calendarEventId),
                    availabilityOfferId: this.availabilityOfferId,
                    from: this.time.from,
                    moderatorNote: this.description,
                    moderatornote: this.moderatornote,
                    offerCategoryIds: this.categories.map((item) => item.id),
                    freeSchedule: this.freeSchedule,
                })
                    .then(() => {
                        this.isConfirmed = true
                        this.isLoading = false
                    })
                    .catch((e) => {
                        const errorMessage = e.error?.invalidFields ? e.error.invalidFields[0]?.message : e.error.message
                        switch (errorMessage) {
                            case CalendarEventEnums.ERRORS.NOT_SUPPORTED_TRANSITION:
                                this.$toast.error(this.$t('modals.confirm_reservation_modal.reschedule_confirmed_reservation'))
                                this.$router.push({ name: RoutesEnum.RESERVATIONS.SINGLE, params: { id: this.calendarEventId } })
                                break
                            case CalendarEventEnums.ERRORS.TIME_MUST_BE_IN_THE_FUTURE:
                                this.handleEventsAfterError(
                                    this.$t('components.reservations.errors.cannot_edit_past_reservations'),
                                )
                                break
                            case CalendarEventEnums.ERRORS.INVALID_AVAILABILITY_OFFER:
                                this.handleEventsAfterError(this.$t('components.reservations.errors.invalid_availability_offer'))
                                this.$router.push({ name: RoutesEnum.INDEX.HOME })
                                break
                            case CalendarEventEnums.ERRORS.UNAVAILABLE_FOR_RESCHEDULING:
                                this.handleEventsAfterError(
                                    this.$t('components.reservations.errors.unavailable_for_rescheduling'),
                                )
                                this.$router.push({ name: RoutesEnum.INDEX.HOME })
                                break
                            case CalendarEventEnums.ERRORS.NOT_PERMISSIONED:
                                this.handleEventsAfterError(this.$t('components.reservations.errors.not_permissioned'))
                                break
                            case CalendarEventEnums.ERRORS.NOT_FOUND:
                                this.handleEventsAfterError(this.$t('components.reservations.errors.not_found'))
                                break
                            case CalendarEventEnums.ERRORS.MINIMUM_DIFFERENCE_IS_20min:
                                if (e.error?.invalidFields[0].field === 'calendarEventId') {
                                    this.handleEventsAfterError(this.$t(`components.reservations.errors.mins_ahead_20`))
                                } else {
                                    this.handleEventsAfterError(this.$t(`modals.confirm_reservation_modal.mins_ahead_20`))
                                }
                                break
                            case CalendarEventEnums.ERRORS.MINIMUM_DIFFERENCE_IS_60min:
                                if (e.error?.invalidFields[0].field === 'calendarEventId') {
                                    this.handleEventsAfterError(this.$t(`components.reservations.errors.mins_ahead_60`))
                                } else {
                                    this.handleEventsAfterError(this.$t(`modals.confirm_reservation_modal.mins_ahead_60`))
                                }
                                break
                            default:
                                this.handleEventsAfterError(this.$t('modals.confirm_reservation_modal.slot_taken'))
                                break
                        }
                    })
            },

            descriptionChange({ value }) {
                if (value === undefined) {
                    return
                }
                this.description = value
            },

            handleEventsAfterError(error) {
                this.$toast.error(error)
                this.emitEvent({ name: 'reservation-failed' })
                this.isLoading = false
            },

            continueReservation(event) {
                this.closeYesNoModal(event.$event)
                this.submit()
            },

            closeYesNoModal(event) {
                if (event) {
                    event.stopPropagation()
                }
                this.isYesNoModalOpen = false
            },
        },

        mounted() {
            isLocalTimeRight().then((res) => {
                if (res) this.handleEventsAfterError(this.$t('components.reservations.errors.time_zone_issue'))
            })
            if (this.moderatorNote?.length) {
                this.description = this.moderatorNote
            }
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .confirm-reservation {
        &__heading {
            font-size: $font-size--heading;
            color: $color--txt-header;
            font-weight: bold;
            text-align: center;
            word-break: break-word;
        }

        &--phone {
            text-align: center;

            &--action {
                @include breakpoint(smartphone-large) {
                    width: 100%;
                    .button {
                        width: 100%;
                    }
                }
            }
            a {
                cursor: pointer;
                color: $color--accent-1;
            }
        }

        &--clients {
            &--toggle {
                display: flex;
                justify-content: center;
                margin-bottom: 20px;
            }
        }

        &__image {
            display: block;
            margin-top: 10px;
            margin-bottom: 20px;
            margin-left: auto;
            margin-right: auto;
            max-width: 40%;
            height: auto;
        }

        &__subtitle {
            font-size: $font-size--secondary;
            text-align: center;
            &--color {
                font-size: $font-size--base;
                color: $color--accent-1-lite;
            }
        }

        &__info {
            font-size: $font-size--medium;
            color: $color--txt-base-3;
            text-align: center;
            word-break: break-word;
        }

        &__parent {
            &:first-child {
                border-top: 1px solid $color-gray-1;
            }

            &:not(:first-child) {
                margin-top: 0px;
            }

            padding: 15px 5px;
            display: flex;
            flex-shrink: 0;

            border-bottom: 1px solid $color-gray-1;

            p {
                line-height: $line-height--modal;
            }

            &--icon {
                min-width: 60px;
            }

            &--heading {
                color: $color--txt-base-3;
                font-size: $font-size--secondary;
                margin-left: 20px;
            }

            &--text {
                margin-left: 20px;
                font-weight: $font-weight--modal;

                &--original-price {
                    font-size: $font-size--hint;
                    opacity: 0.5;
                    text-decoration: line-through;
                }
            }

            &--hidden {
                @include hiddenBlur();
            }
        }
    }

    .subdomain-layout {
        .confirm-reservation {
            &__subtitle {
                &--color {
                    color: $color-main--subdomain;
                }
            }
        }

        &--navy_black,
        &--black {
            .confirm-reservation {
                &__heading {
                    color: $color-white;
                }

                &__parent {
                    &--heading {
                        color: $subdomain-light;
                    }
                }
            }
        }
    }
</style>
